/**
 * useUserAlerts — loads the current user's in-app alerts and keeps them live via a
 * Supabase Realtime subscription on user_alerts (RLS scopes delivered rows to the
 * user). Returns the list, unread count, and helpers.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../services/supabase";
import { useAuth } from "../contexts/AuthContext";
import {
  listUserAlerts,
  markAlertRead,
  markAllAlertsRead,
} from "../services/notifications.service";
import type { UserAlert } from "../types/notifications.types";

export function useUserAlerts(onNew?: (alert: UserAlert) => void) {
  const { user } = useAuth();
  const [alerts, setAlerts] = useState<UserAlert[]>([]);
  const [loading, setLoading] = useState(true);
  const onNewRef = useRef(onNew);
  onNewRef.current = onNew;

  const reload = useCallback(async () => {
    try {
      setAlerts(await listUserAlerts());
    } catch {
      // non-fatal; bell just shows what it has
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // Reliable freshness independent of Realtime: poll periodically + on tab focus.
  // (Realtime below is a best-effort instant layer on top of this.)
  useEffect(() => {
    const id = window.setInterval(() => reload(), 45000);
    const onFocus = () => reload();
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [reload]);

  useEffect(() => {
    if (!user?.id) {
      return;
    }
    const channel = supabase
      .channel(`user_alerts_${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "user_alerts",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as UserAlert;
          setAlerts((prev) =>
            prev.some((a) => a.id === row.id) ? prev : [row, ...prev],
          );
          onNewRef.current?.(row);
        },
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "user_alerts",
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as UserAlert;
          setAlerts((prev) => prev.map((a) => (a.id === row.id ? row : a)));
        },
      )
      .subscribe();
    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id]);

  const markRead = useCallback(async (id: string) => {
    setAlerts((prev) =>
      prev.map((a) => (a.id === id ? { ...a, is_read: true } : a)),
    );
    try {
      await markAlertRead(id);
    } catch {
      /* optimistic; realtime UPDATE will reconcile */
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setAlerts((prev) => prev.map((a) => ({ ...a, is_read: true })));
    try {
      await markAllAlertsRead();
    } catch {
      /* optimistic */
    }
  }, []);

  const unread = alerts.filter((a) => !a.is_read).length;
  return { alerts, unread, loading, reload, markRead, markAllRead };
}
